/**
 * Phase 1 refinement Update 3 — Communications Settings page.
 * Per-template admin-in-CC opt-out + extra CC recipients + exec opt-in.
 * Mounted at:
 *  - /enterprise/settings/communications   (workspace-member)
 *  - /admin/workspace/:id/communications   (platform_admin, AdminLayout)
 */
import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Mail, Loader2, Save, CheckCircle2, X, Plus, Eye, Send } from "lucide-react";

const cls = (...xs) => xs.filter(Boolean).join(" ");

const TEMPLATE_META = {
    assessment_invitation:          { title: "Assessment invitation",      description: "Initial email to each candidate inviting them to take their assessment." },
    day_3_reminder:                 { title: "Day-3 reminder",             description: "Reminder to candidates who haven't started or completed by day 3." },
    day_5_reminder:                 { title: "Day-5 final reminder",       description: "Final reminder before the assessment cycle closes." },
    completion_confirmation:        { title: "Completion confirmation",    description: "Thank-you / confirmation after a candidate submits." },
    right_of_review_disclosure:     { title: "Right-of-review disclosure", description: "Candidate-facing disclosure when results packs are delivered." },
    manager_validation_request:     { title: "Manager validation request", description: "Sent to managers to validate a candidate's role/scope." },
    cohort_enrollment_notification: { title: "Cohort enrollment",          description: "Notifies employee + manager when a Retrain verdict auto-enrolls a cohort." },
    mentor_introduction:            { title: "Mentor introduction",        description: "Introduces a mentor + mentee after a match is created." },
    verdict_pushed_summary:         { title: "Verdict → Equip summary",    description: "Sent when a manager pushes a Worker Diagnostic verdict into the Equip Arm." },
    manager_approval_prompt:        { title: "Manager approval prompt",    description: "Asks a manager/admin to approve an auto-proposed enrollment." },
    welcome_email:                  { title: "Welcome email",              description: "Sent to newly-provisioned workspace admins & execs with login link + temp password." },
};

export default function WorkspaceCommunicationsPage() {
    const { user, api } = useAuth();
    const params = useParams();
    const enterpriseId = params.id || user?.enterprise_id;

    const [data, setData] = useState(null);
    const [draft, setDraft] = useState(null);
    const [busy, setBusy] = useState(false);
    const [savedAt, setSavedAt] = useState(null);

    const refresh = useCallback(() => {
        if (!enterpriseId) return;
        api.get(`/v2/workspace/${enterpriseId}/communications/settings`)
            .then(r => { setData(r.data); setDraft(JSON.parse(JSON.stringify(r.data))); })
            .catch(() => {});
    }, [api, enterpriseId]);

    useEffect(() => { refresh(); }, [refresh]);

    if (!enterpriseId) return <div className="p-8 text-sm text-gray-500">No workspace available.</div>;
    if (!draft) return <div className="p-12 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-gray-500" /></div>;

    const updateTemplate = (i, patch) => {
        setDraft(prev => {
            const next = JSON.parse(JSON.stringify(prev));
            next.templates[i] = { ...next.templates[i], ...patch };
            return next;
        });
    };

    const isDirty = JSON.stringify(draft) !== JSON.stringify(data);

    const save = async () => {
        setBusy(true);
        try {
            await api.put(`/v2/workspace/${enterpriseId}/communications/settings`, {
                default_cc_workspace_admins: draft.default_cc_workspace_admins,
                templates: draft.templates.map(t => ({
                    template_key: t.template_key,
                    cc_workspace_admins: t.cc_workspace_admins,
                    extra_cc: t.extra_cc,
                    opt_in_executive_ids: t.opt_in_executive_ids,
                    opt_out_admin_ids: t.opt_out_admin_ids,
                })),
            });
            setSavedAt(new Date().toLocaleTimeString());
            await refresh();
        } finally { setBusy(false); }
    };

    return (
        <div className="p-8 max-w-4xl space-y-6" data-testid="workspace-communications-page">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-gray-500" />
                        <h1 className="text-2xl font-display font-bold text-white">Communications Settings</h1>
                    </div>
                    <p className="text-sm text-gray-500 mt-1 font-body">
                        Who is CC'd on every candidate + manager + enrollment email. Default: workspace admins copied on everything.
                    </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                    {savedAt && <span className="text-[10px] font-mono text-emerald-400 inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> saved {savedAt}</span>}
                    <button onClick={save} disabled={!isDirty || busy}
                        data-testid="save-comm-btn"
                        className="px-3 py-2 text-xs border border-white/20 text-white hover:bg-white/[0.04] disabled:opacity-40 inline-flex items-center gap-1.5">
                        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                        Save
                    </button>
                </div>
            </div>

            {/* Workspace default */}
            <div className="border border-border/40 bg-card p-4 flex items-start gap-3">
                <input type="checkbox" checked={draft.default_cc_workspace_admins}
                    onChange={e => setDraft(prev => ({ ...prev, default_cc_workspace_admins: e.target.checked }))}
                    data-testid="default-cc-toggle"
                    className="accent-white mt-1" />
                <div>
                    <p className="text-sm text-white">CC workspace admins on every email by default</p>
                    <p className="text-[11px] text-gray-500 mt-0.5">Opt-out per template below. Changes apply to future sends only.</p>
                </div>
            </div>

            {/* Template table */}
            <div className="border border-border/40 bg-card">
                {(draft.templates || []).map((t, i) => (
                    <TemplateRow key={t.template_key} t={t} idx={i} onUpdate={updateTemplate} />
                ))}
            </div>

            {/* Preview / shadow-send */}
            <PreviewPanel enterpriseId={enterpriseId} />
        </div>
    );
}

function PreviewPanel({ enterpriseId }) {
    const { user, api } = useAuth();
    const [tplKey, setTplKey] = useState("assessment_invitation");
    const [candName, setCandName] = useState("Alex Candidate");
    const [candEmail, setCandEmail] = useState("candidate@example.com");
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [drySent, setDrySent] = useState(null);

    const run = async (dry) => {
        setBusy(true); setDrySent(null);
        try {
            const r = await api.post(`/v2/workspace/${enterpriseId}/communications/preview`, {
                template_key: tplKey,
                candidate_name: candName,
                candidate_email: candEmail,
                dry_run_send: dry,
            });
            setResult(r.data);
            if (dry && r.data.dry_run_delivered_to) setDrySent(r.data.dry_run_delivered_to);
        } finally { setBusy(false); }
    };

    return (
        <div className="border border-border/40 bg-card" data-testid="preview-panel">
            <div className="p-4 border-b border-border/40 flex items-center gap-2">
                <Eye className="w-4 h-4 text-gray-500" />
                <h2 className="text-sm font-display text-white">Preview & dry-run</h2>
                <p className="text-[11px] text-gray-500 ml-2">Renders the actual rendered email. Dry-run delivers to <code className="text-gray-300">{user?.email}</code> only — no candidate, no CC list.</p>
            </div>
            <div className="p-4 space-y-3">
                <div className="grid grid-cols-12 gap-2">
                    <div className="col-span-4">
                        <label className="text-[10px] font-mono uppercase text-gray-500">Template</label>
                        <select value={tplKey} onChange={e => setTplKey(e.target.value)} data-testid="preview-template-select"
                            className="w-full mt-1 bg-background border border-border/40 text-xs text-white px-2 py-1.5 focus:outline-none focus:border-white/30">
                            {Object.keys(TEMPLATE_META).map(k => <option key={k} value={k}>{TEMPLATE_META[k].title}</option>)}
                        </select>
                    </div>
                    <div className="col-span-4">
                        <label className="text-[10px] font-mono uppercase text-gray-500">Sample candidate name</label>
                        <input value={candName} onChange={e => setCandName(e.target.value)} data-testid="preview-cand-name"
                            className="w-full mt-1 bg-background border border-border/40 text-xs text-white px-2 py-1.5 focus:outline-none focus:border-white/30" />
                    </div>
                    <div className="col-span-4">
                        <label className="text-[10px] font-mono uppercase text-gray-500">Sample candidate email</label>
                        <input value={candEmail} onChange={e => setCandEmail(e.target.value)} data-testid="preview-cand-email"
                            className="w-full mt-1 bg-background border border-border/40 text-xs text-white px-2 py-1.5 focus:outline-none focus:border-white/30" />
                    </div>
                </div>
                <div className="flex gap-2">
                    <button onClick={() => run(false)} disabled={busy} data-testid="preview-render-btn"
                        className="px-3 py-1.5 text-xs border border-white/20 text-white hover:bg-white/[0.04] disabled:opacity-40 inline-flex items-center gap-1.5">
                        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Eye className="w-3 h-3" />} Render preview
                    </button>
                    <button onClick={() => run(true)} disabled={busy} data-testid="preview-dry-run-btn"
                        className="px-3 py-1.5 text-xs bg-amber-500/20 border border-amber-500/40 text-amber-200 hover:bg-amber-500/30 disabled:opacity-40 inline-flex items-center gap-1.5">
                        <Send className="w-3 h-3" /> Send dry-run to me
                    </button>
                </div>
                {drySent && (
                    <div className="border border-emerald-500/40 bg-emerald-500/10 p-2 text-xs text-emerald-200" data-testid="dry-sent-confirm">
                        Dry-run delivered to <code>{drySent}</code> only. Candidate + CC list were skipped.
                    </div>
                )}
                {result && (
                    <div className="space-y-2 pt-2" data-testid="preview-result">
                        <div className="grid grid-cols-12 gap-2 text-[11px] font-mono">
                            <span className="col-span-2 text-gray-500 uppercase">subject</span>
                            <span className="col-span-10 text-white">{result.subject}</span>
                            <span className="col-span-2 text-gray-500 uppercase">to</span>
                            <span className="col-span-10 text-white">{result.primary_to}</span>
                            <span className="col-span-2 text-gray-500 uppercase">cc</span>
                            <span className="col-span-10 text-white">
                                {(result.cc || []).length === 0 ? <em className="text-gray-600 not-italic">— none —</em> : (result.cc || []).join(", ")}
                            </span>
                        </div>
                        <div className="border border-border/40 bg-white" data-testid="preview-iframe-wrap">
                            <iframe srcDoc={result.html} sandbox="" title="Email preview" className="w-full h-[420px] bg-white" />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function TemplateRow({ t, idx, onUpdate }) {
    const meta = TEMPLATE_META[t.template_key] || { title: t.template_key, description: "" };
    const [newCc, setNewCc] = useState("");
    const addCc = () => {
        const v = newCc.trim();
        if (!v) return;
        onUpdate(idx, { extra_cc: [...(t.extra_cc || []), v] });
        setNewCc("");
    };
    const removeCc = (e) => onUpdate(idx, { extra_cc: (t.extra_cc || []).filter(x => x !== e) });
    return (
        <div className="p-4 border-b border-border/40 space-y-3" data-testid={`template-row-${t.template_key}`}>
            <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                    <p className="text-sm text-white font-medium">{meta.title}</p>
                    <p className="text-[11px] text-gray-500 mt-0.5 font-body">{meta.description}</p>
                </div>
                <label className="flex items-center gap-2 text-xs text-gray-400 flex-shrink-0 cursor-pointer">
                    <input type="checkbox" checked={t.cc_workspace_admins}
                        data-testid={`cc-toggle-${t.template_key}`}
                        onChange={e => onUpdate(idx, { cc_workspace_admins: e.target.checked })}
                        className="accent-white" />
                    <span className="font-mono uppercase text-[10px]">CC admins</span>
                </label>
            </div>
            {/* Extra CC */}
            <div>
                <p className="text-[10px] font-mono uppercase text-gray-500 mb-1.5">Extra recipients</p>
                <div className="flex flex-wrap gap-1.5 items-center">
                    {(t.extra_cc || []).map(e => (
                        <span key={e} className="inline-flex items-center gap-1 bg-background/60 border border-border/40 px-2 py-0.5 text-[11px] text-white font-mono">
                            {e}
                            <button onClick={() => removeCc(e)} className="text-gray-500 hover:text-red-300" data-testid={`remove-cc-${t.template_key}-${e}`}>
                                <X className="w-3 h-3" />
                            </button>
                        </span>
                    ))}
                    <div className="flex items-center gap-1">
                        <input value={newCc} onChange={e => setNewCc(e.target.value)}
                            onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addCc(); } }}
                            placeholder="shared-inbox@example.com"
                            data-testid={`add-cc-input-${t.template_key}`}
                            className="bg-background border border-border/40 text-[11px] text-white px-2 py-1 focus:outline-none focus:border-white/30" />
                        <button onClick={addCc} disabled={!newCc.trim()}
                            data-testid={`add-cc-btn-${t.template_key}`}
                            className="px-1.5 py-1 text-gray-400 hover:text-white disabled:opacity-30 border border-border/40">
                            <Plus className="w-3 h-3" />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
